import { useMutation, useQuery } from "@apollo/client";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SyncIcon from "@mui/icons-material/Sync";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Dialog as MuiDialog,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FilePreview } from "components/Document/shared/FilePreview";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { gql } from "generated-graphql";
import { BulkManifestDiscrepanciesQuery } from "generated-graphql/graphql";
import { useCtrlWheelHorizontalScroll } from "hooks/useCtrlWheelHorizontalScroll";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { CREATE_EXCEPTION_REPORT_SUBMISSION, PROCESSED_MANIFESTS_QUERY } from "./api";
import {
  ALL_DISCREPANCY_STATUSES,
  type DiscrepancyStatus,
  formatDateOnly,
  formatDisplayValue,
  getDiscrepancyStatus,
  getFieldLabel,
  getStatusChipIcon,
  getStatusChipTooltip,
  getStatusColor,
  getStatusLabel,
  isComplianceDiscrepancy,
  isDiscrepancyStatus,
} from "./DiscrepancyReviewDialog.helpers";
import { ProofOfContactDialog } from "./ProofOfContactDialog";
import { useRangeSelection } from "./useRangeSelection";

// --- GraphQL ---

export const BULK_MANIFEST_DISCREPANCIES_QUERY = gql(`
  query BulkManifestDiscrepancies($processedManifestIds: [ID!]!) {
    bulkManifestDiscrepancies(processedManifestIds: $processedManifestIds) {
      id
      processedManifestId
      manifestTrackingNumber
      facilityName
      epaId
      wasteLineUnifiedId
      wasteLineNumber
      field
      isDot
      severity
      status
      eManifestValue
      vendorValue
      manifestDocumentValue
      sourceDocumentId
      sourceDocumentFileExtension
      escalation
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionType
      resolutionNote
      createdAt
    }
  }
`);

const UPDATE_DISCREPANCY_VENDOR_DATA = gql(`
  mutation UpdateDiscrepancyVendorData($discrepancyIds: [ID!]!) {
    updateDiscrepancyVendorData(discrepancyIds: $discrepancyIds) {
      id
      processedManifestId
      field
      status
      eManifestValue
      vendorValue
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionType
      resolutionNote
    }
  }
`);

const RESOLVE_DISCREPANCIES_EXTERNALLY = gql(`
  mutation ResolveDiscrepanciesExternally($discrepancyIds: [ID!]!) {
    resolveDiscrepanciesExternally(discrepancyIds: $discrepancyIds) {
      id
      processedManifestId
      field
      status
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionType
      resolutionNote
    }
  }
`);

const DISMISS_DISCREPANCIES = gql(`
  mutation DismissDiscrepancies($discrepancyIds: [ID!]!, $note: String) {
    dismissDiscrepancies(discrepancyIds: $discrepancyIds, note: $note) {
      id
      processedManifestId
      field
      status
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionType
      resolutionNote
    }
  }
`);

export const COMPLIANCE_MANIFEST_DATA_QUERY = gql(`
  query ComplianceManifestData($search: String, $page: Int, $pageSize: Int) {
    processedManifests(search: $search, page: $page, pageSize: $pageSize) {
      items {
        id
        shippedDate
        transporterContactedAt
        transporterContactedBy
        exceptionReportSubmission {
          id
          title
          status
        }
      }
    }
  }
`);

/**
 * Vertical offsets used for the sticky header chain inside the scrollable
 * dialog left panel. These are fixed pixel values so the section headers can
 * stick below the column headers without needing to measure at runtime.
 */
/**
 * Action bar lives above the split row (outside the table's scroll container),
 * so only the column headers and section headers need explicit sticky offsets.
 * The doc-preview header uses the same height as the column header row so the
 * two line up when the preview is open.
 */
// Expressed in rem so the sticky offset tracks root font-size — MUI v5
// typography is rem-based, so a fixed px value here would desync from the
// actual column-header height at non-default font sizes. 2.3125rem ≈ 37px
// at the default 16px root, which is the small-table header row height.
const STICKY_COLUMN_HEADER_HEIGHT = "2.3125rem";
const STICKY_SECTION_HEADER_TOP = STICKY_COLUMN_HEADER_HEIGHT;

/**
 * Fields where "Update Vendor Data" is not supported.
 * Matches BLOCKED_FIELDS in the resolver.
 */
const UPDATE_VENDOR_DATA_BLOCKED_FIELDS = new Set([
  "eManifestMissing",
  "wasteLinesCount",
  "shippedDate",
  "destinationFacilityId",
]);

// --- Per-action eligibility ---
//
// Each bulk action has backend constraints on which discrepancies it can
// apply to. We mirror those constraints client-side so users see the
// breakdown up front and only eligible IDs are sent to the mutation.
// Source of truth for the rules:
//   - resolveDiscrepancies / dismissDiscrepancies / transitionToPending in
//     packages/encamp-waste-data-access/src/discrepancies/discrepancyWriter.ts
//   - BLOCKED_FIELDS and compliance check in
//     apps/api/src/resolvers/waste/processedManifestCompliance.ts

type ActionKey = "updateVendorData" | "resolveExternally" | "nonDiscrepancy";

type SkipGroup = {
  reason: string;
  count: number;
};

type ActionEligibility = {
  eligibleIds: string[];
  skips: SkipGroup[];
};

function computeActionEligibility(
  action: ActionKey,
  selected: readonly Discrepancy[]
): ActionEligibility {
  const eligibleIds: string[] = [];
  const skipCounts = new Map<string, number>();
  const addSkip = (reason: string) => {
    skipCounts.set(reason, (skipCounts.get(reason) ?? 0) + 1);
  };

  for (const d of selected) {
    if (action === "updateVendorData") {
      if (d.status !== "UNRESOLVED") {
        addSkip("already resolved or pending");
      } else if (UPDATE_VENDOR_DATA_BLOCKED_FIELDS.has(d.field)) {
        addSkip(`${getFieldLabel(d.field)} doesn't support vendor data update`);
      } else {
        eligibleIds.push(d.id);
      }
    } else if (action === "resolveExternally") {
      if (d.status === "RESOLVED") {
        addSkip("already resolved");
      } else if (d.status === "PENDING") {
        addSkip("already pending");
      } else {
        eligibleIds.push(d.id);
      }
    } else {
      // nonDiscrepancy
      if (d.status === "RESOLVED" || d.status === "IGNORED") {
        addSkip("already resolved");
      } else if (isComplianceDiscrepancy(d)) {
        addSkip("compliance row — use Resolve in e-Manifest instead");
      } else {
        eligibleIds.push(d.id);
      }
    }
  }

  const skips = [...skipCounts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);

  return { eligibleIds, skips };
}

/** Render skip summary for tooltip or dialog description. */
function formatSkipSummary(skips: SkipGroup[]): string {
  return skips.map((s) => `• ${s.count} ${s.reason}`).join("\n");
}

// --- Types ---

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

function SectionHeaderRow({
  label,
  count,
  colSpan,
  collapsed,
  onToggle,
}: {
  label: string;
  count: number;
  colSpan: number;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // MUI's `grey` palette is mode-independent (grey[100] is always near-white),
  // so we flip to darker shades in dark mode to avoid bright panels.
  const subtleBg = isDark ? theme.palette.grey[900] : theme.palette.grey[100];
  const subtleHover = isDark ? theme.palette.grey[800] : theme.palette.grey[200];
  const subtleBorder = isDark ? theme.palette.grey[800] : theme.palette.grey[200];
  return (
    <TableRow
      onClick={onToggle}
      sx={{
        cursor: "pointer",
        "&:hover": { bgcolor: subtleHover },
      }}
    >
      <TableCell
        colSpan={colSpan}
        sx={{
          bgcolor: subtleBg,
          py: 0.75,
          px: 0,
          fontWeight: 600,
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${subtleBorder}`,
          // Vertical sticky: each section header sticks below the column
          // headers. Because each SectionHeaderRow lives in its own TableBody,
          // the current section's header pushes the previous one up naturally
          // when the previous group's rows scroll past.
          position: "sticky",
          top: STICKY_SECTION_HEADER_TOP,
          // Below the column header (zIndex 2) but above row content
          zIndex: 1,
        }}
      >
        {/* Inner sticky wrapper keeps the label + chevron pinned to the
            left edge of the viewport during horizontal scroll */}
        <Box
          sx={{
            position: "sticky",
            left: 0,
            width: "fit-content",
            px: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {collapsed ? (
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandLessIcon sx={{ fontSize: 18 }} />
            )}
            <span>{label}</span>
            <Chip size="small" label={count} sx={{ height: 20, fontSize: "0.75rem" }} />
          </Stack>
        </Box>
      </TableCell>
    </TableRow>
  );
}

// --- Bulk action button ---

/**
 * What's stored in state when the user clicks a bulk action button. Kept as
 * plain data (not callbacks) so the Confirm click handler can read current
 * state at confirm-time — including the optional note the user types *after*
 * opening the dialog. Storing a callback here would freeze closure vars like
 * `confirmNote` at click-time and silently drop notes.
 */
type ConfirmActionState = {
  label: string;
  description: string;
  action: ActionKey;
  eligibleIds: string[];
};

interface BulkActionButtonProps {
  label: string;
  /** Icon shown to the left of the label. */
  icon: React.ReactElement;
  /** Plain-language description of what the action does to eligible rows. */
  baseDescription: string;
  /** Pre-computed eligibility derived from the current selection. */
  eligibility: ActionEligibility;
  /** Total number of rows the user has currently selected. */
  totalSelected: number;
  /** Disable while any mutation is in flight. */
  loading: boolean;
  /** Which action this button represents — dispatched at confirm-time. */
  action: ActionKey;
  setConfirmAction: (action: ConfirmActionState | null) => void;
}

/**
 * Bulk action button with per-action eligibility awareness.
 *
 * - Disabled when the user has selected nothing OR when none of the selected
 *   rows are eligible for this action.
 * - Tooltip summarizes the eligibility split (e.g. "3 will be resolved, 2
 *   skipped: field doesn't support vendor data update").
 * - Confirmation dialog description restates the split so the user sees
 *   exactly which rows will run and which will be skipped before committing.
 * - Only eligible IDs reach state (and therefore the mutation) — skipped
 *   rows never hit the backend, so a single "bad" row can't roll back the
 *   whole batch.
 */
function BulkActionButton({
  label,
  icon,
  baseDescription,
  eligibility,
  totalSelected,
  loading,
  action,
  setConfirmAction,
}: BulkActionButtonProps) {
  const { eligibleIds, skips } = eligibility;
  const eligibleCount = eligibleIds.length;
  const skipCount = totalSelected - eligibleCount;
  const nothingSelected = totalSelected === 0;
  const nothingEligible = totalSelected > 0 && eligibleCount === 0;

  let tooltipTitle = "";
  if (nothingSelected) {
    tooltipTitle = "";
  } else if (nothingEligible) {
    tooltipTitle = `None of the ${totalSelected} selected rows support ${label}.\n${formatSkipSummary(skips)}`;
  } else if (skipCount > 0) {
    tooltipTitle = `${eligibleCount} of ${totalSelected} will run. ${skipCount} will be skipped:\n${formatSkipSummary(skips)}`;
  }

  const rowWord = (n: number) => (n === 1 ? "row" : "rows");
  const description =
    skipCount > 0
      ? `${baseDescription}\n\n${eligibleCount} of ${totalSelected} selected ${rowWord(totalSelected)} will be processed. ${skipCount} will be skipped:\n${formatSkipSummary(skips)}`
      : `${baseDescription}\n\nThis will apply to ${eligibleCount} selected ${rowWord(eligibleCount)}.`;

  return (
    <Tooltip
      title={tooltipTitle}
      placement="top"
      enterDelay={300}
      slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
    >
      <span>
        <Button
          variant="outlined"
          size="small"
          color="warning"
          startIcon={icon}
          disabled={nothingSelected || nothingEligible || loading}
          onClick={() =>
            setConfirmAction({
              label,
              description,
              action,
              eligibleIds,
            })
          }
        >
          {label}
        </Button>
      </span>
    </Tooltip>
  );
}

// --- Main component ---

interface DiscrepancyReviewDialogProps {
  processedManifestIds: string[];
  /** Optional manifest tracking number for single-manifest title display */
  manifestTrackingNumber?: string;
  /** When provided, only show discrepancies for these waste line IDs (line-item mode) */
  wasteLineUnifiedIds?: string[];
  onClose: () => void;
}

export function DiscrepancyReviewDialog({
  processedManifestIds,
  manifestTrackingNumber,
  wasteLineUnifiedIds,
  onClose,
}: DiscrepancyReviewDialogProps) {
  const { tenantId } = useParams<{ tenantId: string }>();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // Full-panel bg for non-sticky surfaces (action bar). In light mode this
  // is the dialog's paper white; in dark mode we go transparent so the
  // parent Dialog's elevated surface bleeds through — that's the surrounding
  // grey the user wants to match, and setting a literal `background.paper`
  // here would render as a black slab because Dialog paints its grey via
  // an elevation overlay, not via the raw paper color.
  const panelBg = isDark ? "transparent" : theme.palette.background.paper;
  // Sticky surfaces (TableHead, section headers) need to be opaque to cover
  // scrolling rows underneath. In dark mode we replicate MUI Paper's
  // elevation-24 overlay — a 16%-white layer on top of background.paper —
  // so the sticky surface renders as the same grey as the dialog.
  const stickyOpaquePanelBg = isDark
    ? {
        backgroundColor: theme.palette.background.paper,
        backgroundImage: `linear-gradient(${alpha(
          theme.palette.common.white,
          0.16
        )}, ${alpha(theme.palette.common.white, 0.16)})`,
      }
    : { backgroundColor: theme.palette.background.paper };
  const subtleSurfaceBg = isDark ? theme.palette.grey[900] : theme.palette.grey[50];
  const rowDividerColor = isDark ? theme.palette.grey[800] : theme.palette.grey[100];
  const alerts = useAlerts();
  const [statusFilter, setStatusFilter] = useState<DiscrepancyStatus[]>(() => [
    ...ALL_DISCREPANCY_STATUSES,
  ]);

  const tableScrollRef = useCtrlWheelHorizontalScroll();

  // --- Document preview panel state ---
  //
  // Single source of truth: the document the user has *asked* to preview.
  // `previewDoc` (the rendered preview) is derived from this target plus
  // Apollo's keyed useQuery result. When the user clicks a different doc,
  // the `variables` change and any in-flight response for the previous
  // target is discarded by Apollo — so a slow fetch for doc A can't
  // clobber the preview after the user has moved on to doc B.
  const [previewTarget, setPreviewTarget] = useState<{
    documentId: string;
    fileExtension: string;
  } | null>(null);

  const { data: previewUrlData, loading: previewUrlLoading } = useQuery(
    GET_DOCUMENT_DOWNLOAD_LINK_QUERY,
    {
      variables: { id: previewTarget?.documentId ?? "" },
      skip: !previewTarget,
      fetchPolicy: "network-only",
      onError: () => {
        // Clear the target so `previewLoading` doesn't stay true forever —
        // the derived `previewDoc` only becomes non-null on a successful
        // fetch, so without this reset the panel would hang in its loading
        // skeleton until the user manually closed the preview.
        alerts.error("Failed to load document preview");
        setPreviewTarget(null);
      },
    }
  );

  /** The rendered preview — only populated once Apollo has resolved the URL
   *  for the current target. While loading/unset, this is null. */
  const previewDoc = useMemo(() => {
    if (!previewTarget) return null;
    if (previewUrlLoading) return null;
    const url = previewUrlData?.getDocumentDownloadLink;
    if (!url) return null;
    return {
      documentId: previewTarget.documentId,
      fileExtension: previewTarget.fileExtension,
      url,
    };
  }, [previewTarget, previewUrlLoading, previewUrlData]);

  /** True while a preview has been requested but the URL hasn't arrived yet. */
  const previewLoading = previewTarget != null && previewDoc == null;

  /** Document id the UI should treat as "being previewed" right now —
   *  this is always the target's id, regardless of URL-fetch progress,
   *  so row highlights / eyeball icons activate synchronously on click. */
  const activePreviewDocId = previewTarget?.documentId ?? null;

  const openDocumentPreview = useCallback(
    (documentId: string, fileExtension: string | null | undefined) => {
      // Functional setter keeps the "same doc → no-op" guard without listing
      // previewTarget in the deps, so the callback identity stays stable
      // across preview changes (protects against future React.memo'd rows).
      setPreviewTarget((prev) =>
        prev?.documentId === documentId
          ? prev
          : { documentId, fileExtension: fileExtension ?? "pdf" }
      );
    },
    []
  );

  const openDocumentInNewTab = useCallback(() => {
    if (!previewDoc?.url) return;
    window.open(previewDoc.url, "_blank");
  }, [previewDoc]);

  // Use operation name strings so Apollo refetches all active instances
  // regardless of variable differences (list vs drawer vs dialog).
  const parentRefetchQueries = ["ProcessedManifests", "ComplianceManifestData"];

  const [updateVendorData, { loading: updatingVendorData }] = useMutation(
    UPDATE_DISCREPANCY_VENDOR_DATA,
    { refetchQueries: parentRefetchQueries }
  );

  const [resolveExternally, { loading: resolvingExternally }] = useMutation(
    RESOLVE_DISCREPANCIES_EXTERNALLY,
    { refetchQueries: parentRefetchQueries }
  );

  const [dismiss, { loading: dismissing }] = useMutation(DISMISS_DISCREPANCIES, {
    // Also refetch the discrepancy list since IGNORED items are filtered
    // server-side and Apollo's cache merge won't remove them from the array.
    refetchQueries: [...parentRefetchQueries, "BulkManifestDiscrepancies"],
  });

  const mutationLoading = updatingVendorData || resolvingExternally || dismissing;

  const [confirmAction, setConfirmAction] = useState<ConfirmActionState | null>(null);
  const [confirmNote, setConfirmNote] = useState("");

  const { data, loading } = useQuery(BULK_MANIFEST_DISCREPANCIES_QUERY, {
    variables: { processedManifestIds },
  });

  // Fetch compliance manifest data for exception report / proof-of-contact actions
  const compliancePmIds = useMemo(() => {
    const raw = data?.bulkManifestDiscrepancies ?? [];
    const ids = new Set<string>();
    for (const d of raw) {
      if (isComplianceDiscrepancy(d)) {
        ids.add(d.processedManifestId);
      }
    }
    return [...ids];
  }, [data?.bulkManifestDiscrepancies]);

  const { data: complianceData } = useQuery(COMPLIANCE_MANIFEST_DATA_QUERY, {
    variables: {
      search: `tenantId:${tenantId} id:${compliancePmIds.join("⁋")}`,
      page: 0,
      pageSize: compliancePmIds.length,
    },
    skip: compliancePmIds.length === 0 || !tenantId,
  });

  const complianceManifestMap = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof complianceData>["processedManifests"]["items"][number]
    >();
    for (const item of complianceData?.processedManifests?.items ?? []) {
      map.set(item.id, item);
    }
    return map;
  }, [complianceData]);

  const [createExceptionReport, { loading: creatingExceptionReport }] = useMutation(
    CREATE_EXCEPTION_REPORT_SUBMISSION,
    {
      refetchQueries: [
        PROCESSED_MANIFESTS_QUERY,
        BULK_MANIFEST_DISCREPANCIES_QUERY,
        COMPLIANCE_MANIFEST_DATA_QUERY,
      ],
    }
  );

  const [showProofOfContactForPmId, setShowProofOfContactForPmId] = useState<string | null>(null);

  const allDiscrepancies = useMemo(() => {
    const raw = data?.bulkManifestDiscrepancies ?? [];
    if (!wasteLineUnifiedIds) return raw;
    const wluSet = new Set(wasteLineUnifiedIds);
    return raw.filter((d) => d.wasteLineUnifiedId && wluSet.has(d.wasteLineUnifiedId));
  }, [data?.bulkManifestDiscrepancies, wasteLineUnifiedIds]);

  const isLineItemMode = Boolean(wasteLineUnifiedIds);
  const isSingleManifest = processedManifestIds.length === 1;

  let title: string;
  if (isLineItemMode) {
    title = `Line Item Discrepancies — ${wasteLineUnifiedIds?.length ?? 0} line items`;
  } else if (isSingleManifest) {
    title = `Manifest Discrepancies — #${manifestTrackingNumber ?? processedManifestIds[0].slice(0, 8)}`;
  } else {
    title = `Bulk Discrepancy Review — ${processedManifestIds.length} manifests`;
  }

  // Apply status filter
  const discrepancies = useMemo(() => {
    if (statusFilter.length === 0) return [];
    return allDiscrepancies.filter((d) => statusFilter.includes(getDiscrepancyStatus(d)));
  }, [allDiscrepancies, statusFilter]);

  const {
    selectedIds,
    toggle: toggleSelect,
    clear: clearSelection,
  } = useRangeSelection({ items: discrepancies });

  // Dispatch the confirmed bulk action. Reads confirmAction + confirmNote from
  // current state at invocation-time — the TextField for the note lives inside
  // the confirmation dialog, so the user types the note AFTER opening it.
  // Pre-binding the action to a callback at click-time (like we used to) would
  // freeze confirmNote to its click-time value and silently drop the note.
  const handleConfirm = useCallback(async () => {
    if (!confirmAction) return;
    const { action, eligibleIds, label } = confirmAction;
    const note = confirmNote || null;
    try {
      if (action === "updateVendorData") {
        await updateVendorData({ variables: { discrepancyIds: eligibleIds } });
        alerts.success(`${eligibleIds.length} discrepancies resolved — vendor data updated`);
      } else if (action === "resolveExternally") {
        await resolveExternally({ variables: { discrepancyIds: eligibleIds } });
        alerts.success(
          `${eligibleIds.length} discrepancies marked as pending e-Manifest resolution`
        );
      } else {
        await dismiss({ variables: { discrepancyIds: eligibleIds, note } });
        alerts.success(`${eligibleIds.length} items dismissed as non-discrepancies`);
      }
      clearSelection();
    } catch (err) {
      alerts.error(`Failed to ${label.toLowerCase()}`, err instanceof Error ? err : undefined);
    }
    setConfirmAction(null);
    setConfirmNote("");
  }, [
    confirmAction,
    confirmNote,
    updateVendorData,
    resolveExternally,
    dismiss,
    alerts,
    clearSelection,
  ]);

  // Group by field name
  const fieldGroups = useMemo(() => {
    const groups = new Map<string, Discrepancy[]>();
    for (const d of discrepancies) {
      const existing = groups.get(d.field);
      if (existing) {
        existing.push(d);
      } else {
        groups.set(d.field, [d]);
      }
    }
    return [...groups.entries()].map(([field, items]) => ({
      field,
      label: getFieldLabel(field),
      items,
    }));
  }, [discrepancies]);

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (field: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  };

  const selectedDiscrepancies = useMemo(
    () => discrepancies.filter((d) => selectedIds.has(d.id)),
    [discrepancies, selectedIds]
  );

  const updateVendorDataEligibility = useMemo(
    () => computeActionEligibility("updateVendorData", selectedDiscrepancies),
    [selectedDiscrepancies]
  );
  const resolveExternallyEligibility = useMemo(
    () => computeActionEligibility("resolveExternally", selectedDiscrepancies),
    [selectedDiscrepancies]
  );
  const nonDiscrepancyEligibility = useMemo(
    () => computeActionEligibility("nonDiscrepancy", selectedDiscrepancies),
    [selectedDiscrepancies]
  );

  const COL_COUNT = 7; // checkbox, facility, manifest, eManifest, vendor, document, status

  const renderValueCell = (
    value: string | null | undefined,
    discrepancy: Discrepancy,
    source: "eManifest" | "vendor"
  ) => {
    const display =
      discrepancy.field === "shippedDate" ? formatDateOnly(value) : formatDisplayValue(value);
    return (
      <TableCell
        sx={{
          maxWidth: 240,
          wordBreak: "break-word",
        }}
      >
        <Typography variant="body2" sx={{ pt: "4px" }}>
          {display}
        </Typography>
      </TableCell>
    );
  };

  const renderComplianceActions = (d: Discrepancy) => {
    const pm = complianceManifestMap.get(d.processedManifestId);

    if (d.escalation === "OVERDUE_60") {
      if (pm?.exceptionReportSubmission) {
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2">{pm.exceptionReportSubmission.title}</Typography>
            <Button
              size="small"
              variant="text"
              href={`/o/${tenantId}/waste/submissions/${pm.exceptionReportSubmission.id}`}
            >
              View
            </Button>
          </Stack>
        );
      }
      return (
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={creatingExceptionReport}
          onClick={async () => {
            try {
              await createExceptionReport({
                variables: { processedManifestId: d.processedManifestId },
              });
              alerts.success("Exception report submission created");
            } catch {
              alerts.error("Failed to create exception report submission");
            }
          }}
        >
          Create Exception Report
        </Button>
      );
    }

    if (d.escalation === "OVERDUE_45") {
      if (pm?.transporterContactedAt) {
        return (
          <Typography variant="body2">
            Contacted {pm.transporterContactedBy ? `by ${pm.transporterContactedBy}` : ""} on{" "}
            {getUtcDisplayDate(pm.transporterContactedAt, "med")}
          </Typography>
        );
      }
      return (
        <Button
          size="small"
          variant="outlined"
          onClick={() => setShowProofOfContactForPmId(d.processedManifestId)}
        >
          Add Proof of Contact
        </Button>
      );
    }

    return null;
  };

  const renderDiscrepancyRow = (d: Discrepancy) => {
    const status = getDiscrepancyStatus(d);
    const isSelected = selectedIds.has(d.id);
    const isComplianceRow = isComplianceDiscrepancy(d);
    const isPreviewedDoc =
      activePreviewDocId != null &&
      d.sourceDocumentId != null &&
      d.sourceDocumentId === activePreviewDocId;

    // Row background precedence:
    //   selected (primary.focusVisible, 0.30)
    //     > previewed doc (primary.selected, 0.08)
    //     > default row
    // Hover bumps the opacity by MUI's standard hoverOpacity (0.04).
    // Divider color tracks the row background — a primary-tinted line on
    // highlighted rows, subtle grey on plain rows — so the divider stays
    // visible against each background without ever washing out.
    let baseBgcolor: string | undefined;
    let hoverBgcolor: string;
    let dividerColor: string;
    if (isSelected) {
      baseBgcolor = alpha(theme.palette.primary.main, 0.2);
      hoverBgcolor = alpha(theme.palette.primary.main, 0.24);
      dividerColor = alpha(theme.palette.primary.main, 0.1);
    } else if (isPreviewedDoc) {
      baseBgcolor = alpha(theme.palette.primary.main, 0.08);
      hoverBgcolor = alpha(theme.palette.primary.main, 0.12);
      dividerColor = alpha(theme.palette.primary.main, 0.1);
    } else {
      baseBgcolor = undefined;
      hoverBgcolor = theme.palette.action.hover;
      dividerColor = rowDividerColor;
    }

    return (
      <TableRow
        key={d.id}
        selected={isSelected}
        onMouseDown={(event) => {
          // Suppress the browser's native shift-click text-selection that
          // would otherwise highlight everything between the anchor and
          // target rows. Only fires on shift so non-shift text selection
          // (for copy/paste) still works.
          if (event.shiftKey) {
            event.preventDefault();
          }
        }}
        onClick={(event) => {
          // Clicking anywhere on the row toggles selection — but let
          // interactive descendants (checkbox, document icon button, chip
          // tooltip trigger, etc.) handle their own clicks first.
          if (
            event.target instanceof Element &&
            event.target.closest('button, a, input, [role="button"]')
          ) {
            return;
          }
          toggleSelect(d.id, { shiftKey: event.shiftKey });
        }}
        sx={{
          cursor: "pointer",
          "& .MuiTableCell-body": {
            borderBottomColor: dividerColor,
          },
          ...(baseBgcolor != null ? { bgcolor: baseBgcolor } : {}),
          // Override MUI's default `Mui-selected` tinting so our primary
          // color wins over `action.selected`. Single-source the color
          // from the ladder above so this override tracks future tweaks.
          ...(isSelected
            ? {
                "&.Mui-selected": { bgcolor: baseBgcolor },
                "&.Mui-selected:hover": { bgcolor: hoverBgcolor },
              }
            : {}),
          "&:hover": {
            bgcolor: hoverBgcolor,
          },
        }}
      >
        {/* Checkbox */}
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={(e) => {
              // shiftKey lives on the native MouseEvent / KeyboardEvent that
              // triggered the change — not on the synthetic ChangeEvent.
              const nativeEvt = e.nativeEvent;
              const shiftKey =
                nativeEvt instanceof MouseEvent || nativeEvt instanceof KeyboardEvent
                  ? nativeEvt.shiftKey
                  : false;
              toggleSelect(d.id, { shiftKey });
            }}
            // Nudge the checkbox down to align its visual center with the
            // first line of the facility name (the neighboring cells have
            // top padding the checkbox cell doesn't).
            sx={{ mt: "12px" }}
          />
        </TableCell>

        {/* Facility */}
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="body2" sx={{ pt: "4px" }}>
            {d.facilityName ?? "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {d.epaId}
          </Typography>
        </TableCell>

        {/* Manifest tracking number */}
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="body2" fontWeight={500} sx={{ pt: "4px" }}>
            {d.manifestTrackingNumber ?? d.processedManifestId.slice(0, 8)}
          </Typography>
          {d.wasteLineUnifiedId && (
            <Typography variant="caption" color="text.secondary">
              Line {d.wasteLineNumber ?? "item"}
            </Typography>
          )}
        </TableCell>

        {isComplianceRow ? (
          <>
            {/* Days elapsed */}
            <TableCell>
              <Typography variant="body2" color="error">
                {(() => {
                  const pm = complianceManifestMap.get(d.processedManifestId);
                  if (!pm?.shippedDate) return "—";
                  const days = Math.floor(
                    (Date.now() - new Date(pm.shippedDate).getTime()) / (1000 * 60 * 60 * 24)
                  );
                  return `${days} days`;
                })()}
              </Typography>
            </TableCell>

            {/* Action / status */}
            <TableCell colSpan={2}>{renderComplianceActions(d)}</TableCell>
          </>
        ) : (
          <>
            {/* eManifest value */}
            {renderValueCell(d.eManifestValue, d, "eManifest")}

            {/* Vendor value */}
            {renderValueCell(d.vendorValue, d, "vendor")}

            {/* Document value */}
            <TableCell sx={{ maxWidth: 240, wordBreak: "break-word" }}>
              <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                {d.sourceDocumentId && (
                  <Tooltip title={isPreviewedDoc ? "Viewing document" : "Preview source document"}>
                    <IconButton
                      size="small"
                      // Remove left padding so the icon hugs the cell's
                      // left edge, matching where the "—" sits in rows
                      // without a source document.
                      sx={{ pl: 0 }}
                      onClick={() => {
                        if (d.sourceDocumentId) {
                          openDocumentPreview(d.sourceDocumentId, d.sourceDocumentFileExtension);
                        }
                      }}
                    >
                      {isPreviewedDoc ? (
                        <VisibilityIcon sx={{ fontSize: 16 }} color="primary" />
                      ) : (
                        <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <Typography
                  variant="body2"
                  // Nudge the text down so its first line aligns with the
                  // icon's visual center (the IconButton is slightly taller
                  // than the body2 line box).
                  sx={{ pt: "4px" }}
                >
                  {d.field === "shippedDate"
                    ? formatDateOnly(d.manifestDocumentValue)
                    : formatDisplayValue(d.manifestDocumentValue)}
                </Typography>
              </Stack>
            </TableCell>
          </>
        )}

        {/* Status */}
        <TableCell>
          {(() => {
            const tooltipText = getStatusChipTooltip(d, status);
            const statusIcon = getStatusChipIcon(d, status);
            // Use the Chip's `icon` slot (decorative) and flip flex order
            // so the glyph still renders on the right of the label. The
            // `icon` slot is a non-interactive adornment — unlike the
            // `deleteIcon` slot, which MUI wraps in a click handler that
            // assistive tech announces as a "delete" button, misrepresenting
            // a purely decorative icon.
            const chip = (
              <Chip
                size="small"
                label={getStatusLabel(status)}
                color={getStatusColor(status)}
                variant="outlined"
                icon={
                  statusIcon ? React.cloneElement(statusIcon, { "aria-hidden": true }) : undefined
                }
                sx={{
                  cursor: "default",
                  mt: "2px",
                  flexDirection: "row-reverse",
                  bgcolor: "background.paper",
                  "& .MuiChip-icon": {
                    // Wrapping the passed icon in a <Box> caused the
                    // fontSize rule to miss the inner SVG — cloning the
                    // icon directly makes `.MuiChip-icon` land on the SVG.
                    fontSize: 14,
                    opacity: 0.7,
                    // flex-direction: row-reverse swaps left/right, so pull
                    // the normal icon-left margins over to the right side.
                    marginRight: "5px",
                    marginLeft: "-2px",
                  },
                }}
              />
            );
            return tooltipText ? <Tooltip title={tooltipText}>{chip}</Tooltip> : chip;
          })()}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth={(previewDoc ?? previewLoading) ? false : "xl"}
        fullWidth
        slotProps={{
          paper: {
            sx: {
              maxHeight: "90vh",
              height: "90vh",
              ...((previewDoc ?? previewLoading) ? { maxWidth: "95vw", width: "95vw" } : {}),
            },
          },
        }}
        title={title}
        noPrimaryAction
        titleBarActionElement={(() => {
          const allSelectedInFilter = ALL_DISCREPANCY_STATUSES.every((s) =>
            statusFilter.includes(s)
          );
          const allStatuses = [...ALL_DISCREPANCY_STATUSES];
          // `"__all__"` is a menu-item sentinel; typing the Select as
          // `<string[]>` keeps `onChange` and `renderValue` strongly typed
          // (no `as` casts) while still accepting the narrower
          // `DiscrepancyStatus[]` state as its value.
          return (
            <FormControl size="small" sx={{ width: 220, position: "relative" }}>
              <InputLabel>Status</InputLabel>
              <Select<string[]>
                multiple
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  const raw = e.target.value;
                  // MUI emits a comma-joined string in native-select mode;
                  // for a multi-value MUI select it's already a string[].
                  const rawArray: string[] = typeof raw === "string" ? raw.split(",") : raw;

                  // "Select all" sentinel — toggle based on current state
                  if (rawArray.includes("__all__")) {
                    setStatusFilter(allSelectedInFilter ? [] : allStatuses);
                    return;
                  }

                  setStatusFilter(rawArray.filter(isDiscrepancyStatus));
                }}
                // Show "All selected" when every status is checked;
                // otherwise list the selected labels inline. The fixed
                // FormControl width keeps the input from resizing.
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return "None selected";
                  }
                  if (selected.length === ALL_DISCREPANCY_STATUSES.length) {
                    return "All selected";
                  }
                  return selected
                    .filter(isDiscrepancyStatus)
                    .map((s) => getStatusLabel(s))
                    .join(", ");
                }}
              >
                <MenuItem value="__all__">
                  <Checkbox
                    size="small"
                    checked={allSelectedInFilter}
                    indeterminate={!allSelectedInFilter && statusFilter.length > 0}
                  />
                  <ListItemText primary="Select all" />
                </MenuItem>
                {ALL_DISCREPANCY_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    <Checkbox size="small" checked={statusFilter.includes(s)} />
                    <ListItemText primary={getStatusLabel(s)} />
                  </MenuItem>
                ))}
              </Select>
              {statusFilter.length === 0 && (
                <FormHelperText
                  sx={{
                    // Float the helper text out of flow so long copy wrapping
                    // doesn't push the dialog content below it downward.
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    m: 0,
                    mt: 0.5,
                  }}
                >
                  Select at least one status to view data
                </FormHelperText>
              )}
            </FormControl>
          );
        })()}
        contentSx={{ overflow: "hidden" }}
      >
        {/* position: relative wrapper gives children a concrete height to resolve against */}
        <Box sx={{ flex: 1, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Action bar — full width of the dialog, above the split row.
                Sits outside the table's scroll container so it spans both
                the table panel and the document-preview panel when open. */}
            <Box
              sx={{
                flexShrink: 0,
                bgcolor: panelBg,
                overflowX: "auto",
                // Hide the scrollbar while preserving scrollability — users
                // can still drag/swipe to reach overflowing buttons
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  width: "fit-content",
                  p: 1,
                }}
              >
                <BulkActionButton
                  label="Correct Vendor Data"
                  icon={<CheckIcon />}
                  baseDescription="Overwrite vendor data with e-Manifest values. Eligible rows will be marked as resolved."
                  eligibility={updateVendorDataEligibility}
                  totalSelected={selectedIds.size}
                  loading={mutationLoading}
                  action="updateVendorData"
                  setConfirmAction={setConfirmAction}
                />
                <BulkActionButton
                  label="Resolve in e-Manifest"
                  icon={<SyncIcon />}
                  baseDescription={
                    'Mark as being resolved in e-Manifest (e.g. updating the value in RCRAInfo). These will move to "Pending" until the change propagates back through the system.'
                  }
                  eligibility={resolveExternallyEligibility}
                  totalSelected={selectedIds.size}
                  loading={mutationLoading}
                  action="resolveExternally"
                  setConfirmAction={setConfirmAction}
                />
                <BulkActionButton
                  label="Non-Discrepancy"
                  icon={<CloseIcon />}
                  baseDescription="Mark as non-discrepancies. The difference is expected and will be hidden from view."
                  eligibility={nonDiscrepancyEligibility}
                  totalSelected={selectedIds.size}
                  loading={mutationLoading}
                  action="nonDiscrepancy"
                  setConfirmAction={setConfirmAction}
                />
              </Stack>
            </Box>

            {/* Split row — table panel on the left, optional document
                preview panel on the right */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "row",
                overflow: "hidden",
              }}
            >
              {/* Left panel — discrepancy table */}
              <Box
                ref={tableScrollRef}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "auto",
                }}
              >
                {loading ? (
                  <Stack spacing={2} sx={{ p: 1 }}>
                    {/* Skeleton table rows */}
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Skeleton variant="rectangular" width={18} height={18} />
                          </TableCell>
                          {[
                            "Facility",
                            "Manifest",
                            "e-Manifest",
                            "Vendor",
                            "Document",
                            "Status",
                          ].map((h) => (
                            <TableCell key={h}>
                              <Skeleton variant="text" width={80} />
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Array.from({ length: 8 }, (_, i) => (
                          <TableRow key={i}>
                            <TableCell padding="checkbox">
                              <Skeleton variant="rectangular" width={18} height={18} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="text" width={120} />
                              <Skeleton variant="text" width={80} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="text" width={100} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="text" width={140} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="text" width={140} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="text" width={60} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="rounded" width={80} height={24} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Stack>
                ) : (
                  <>
                    {/* Comparison table — overflow handled by parent scroll container
                      so the horizontal scrollbar stays at the bottom of the viewport.
                      Column headers stick at the top of the left panel scroll container.
                      Section headers are in separate <TableBody> elements so each one
                      naturally pushes the previous section header up when its group
                      scrolls away. */}
                    <TableContainer sx={{ overflow: "visible" }}>
                      <Table
                        size="small"
                        stickyHeader
                        sx={{
                          // Column header row sticks to the top of the scroll
                          // container (the action bar lives above, outside this
                          // container, so there's nothing to offset against).
                          // Keep a 1px border in the header's background color
                          // so the sticky header stays fully opaque (no
                          // transparent seam for content to bleed through)
                          // while still appearing borderless per design.
                          "& .MuiTableHead-root .MuiTableCell-head": {
                            top: 0,
                            // Override MUI's default stickyHeader bg (which
                            // uses background.default — near-black in dark
                            // mode). Light mode = white; dark mode =
                            // background.paper + the elevation-24 overlay,
                            // so the sticky header reads as the same grey
                            // as the dialog instead of a black slab.
                            ...stickyOpaquePanelBg,
                            borderBottom: `1px solid ${theme.palette.background.paper}`,
                          },
                          // Top-align body cell contents so multi-line values
                          // (e.g. long waste descriptions) line up across the
                          // row instead of floating at vertical center.
                          "& .MuiTableBody-root .MuiTableCell-body": {
                            verticalAlign: "top",
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox" />

                            <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Facility</TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Manifest</TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
                              e-Manifest
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Vendor</TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Document</TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        {fieldGroups.map((group) => (
                          <TableBody key={group.field}>
                            <SectionHeaderRow
                              label={group.label}
                              count={group.items.length}
                              colSpan={COL_COUNT}
                              collapsed={collapsedGroups.has(group.field)}
                              onToggle={() => toggleGroup(group.field)}
                            />
                            {!collapsedGroups.has(group.field) &&
                              group.items.map(renderDiscrepancyRow)}
                          </TableBody>
                        ))}

                        {discrepancies.length === 0 && (
                          <TableBody>
                            <TableRow>
                              <TableCell
                                colSpan={COL_COUNT}
                                align="center"
                                sx={{
                                  py: 8,
                                  bgcolor: subtleSurfaceBg,
                                  borderBottom: "none",
                                }}
                              >
                                <Typography variant="body2" color="text.secondary">
                                  {(() => {
                                    if (statusFilter.length === 0) {
                                      return "Select at least one status to view data";
                                    }
                                    if (statusFilter.length < ALL_DISCREPANCY_STATUSES.length) {
                                      return "No discrepancies found matching selected status";
                                    }
                                    if (isSingleManifest) {
                                      return "No discrepancies found for this manifest";
                                    }
                                    return "No discrepancies found for the selected manifests";
                                  })()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        )}
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>

              {/* Right panel — document preview */}
              {(previewDoc ?? previewLoading) && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 2,
                        // Match the table's sticky column header height so the
                        // top of the PDF preview visually aligns with the top
                        // of the first data row
                        height: STICKY_COLUMN_HEADER_HEIGHT,
                        flexShrink: 0,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: subtleSurfaceBg,
                      }}
                    >
                      <Typography variant="subtitle2">Source Document</Typography>
                      <Stack direction="row" spacing={0.5}>
                        {previewDoc && (
                          <Tooltip title="Open in new tab">
                            <IconButton size="small" onClick={openDocumentInNewTab}>
                              <OpenInNewIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Close preview">
                          <IconButton size="small" onClick={() => setPreviewTarget(null)}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                    <Box sx={{ flex: 1, overflow: "hidden" }}>
                      {previewLoading ? (
                        <Skeleton
                          variant="rectangular"
                          animation="wave"
                          height="100%"
                          width="100%"
                        />
                      ) : previewDoc ? (
                        <FilePreview
                          fileUrl={previewDoc.url}
                          fileExtension={previewDoc.fileExtension}
                        />
                      ) : null}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Dialog>

      {showProofOfContactForPmId && (
        <ProofOfContactDialog
          open
          onClose={() => setShowProofOfContactForPmId(null)}
          processedManifestId={showProofOfContactForPmId}
        />
      )}

      {/* Confirmation dialog */}
      <MuiDialog
        open={confirmAction !== null}
        onClose={() => {
          setConfirmAction(null);
          setConfirmNote("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmAction?.label}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, whiteSpace: "pre-line" }}>
            {confirmAction?.description}
          </DialogContentText>
          <TextField
            label="Note (optional)"
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
            value={confirmNote}
            onChange={(e) => setConfirmNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmAction(null);
              setConfirmNote("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={mutationLoading}
            onClick={handleConfirm}
          >
            {confirmAction?.label}
          </Button>
        </DialogActions>
      </MuiDialog>
    </>
  );
}
